import * as React from "react";
import { Link } from "@tanstack/react-router";
import type { Post } from "@/lib/blog/getPost";
import { getFocalPointStyle, formatDate } from "@/utilities/blog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Heading } from "@/components/ui/heading";

type BlogCardProps = {
  post: Post;
};

export function BlogCard({ post }: BlogCardProps) {
  return (
    <Link to="/blog/$slug" params={{ slug: post.slug }}>
      <Card>
        {post.image && (
          <div className="aspect-video min-h-[280px] w-full overflow-hidden">
            <img
              src={post.image}
              alt={post.keywords.join(", ") || post.title}
              className="w-full h-full object-cover rounded-t-xl"
              style={getFocalPointStyle(post)}
            />
          </div>
        )}
        <CardContent>
          <Heading level="h2" size="h4" className="mb-2 line-clamp-2">
            <span className="text-primary group-hover:text-primary/80 transition-colors">
              {post.title}
            </span>
          </Heading>
          <p className="text-muted-foreground text-sm mb-3">{formatDate(post.date)}</p>
          <p className="text-muted-foreground line-clamp-3 mb-4">{post.summary}</p>
          <div className="flex flex-wrap gap-2 mb-4">
            {post.keywords?.slice(0, 3).map((keyword: string) => (
              <span
                key={`keyword-${keyword}`}
                className="bg-primary/10 text-primary text-xs px-2 py-1 rounded-full capitalize"
              >
                {keyword}
              </span>
            ))}
          </div>
          <Button type="button" variant="outline">
            Read more
            <svg
              className="w-4 h-4 ml-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <title>Right arrow</title>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M14 5l7 7m0 0l-7 7m7-7H3"
              />
            </svg>
          </Button>
        </CardContent>
      </Card>
    </Link>
  );
}
